import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import moment, { Moment, isMoment } from 'moment'
import { MatCalendar } from '@angular/material/datepicker'
import {
  CalendarMaxDate,
  EndOFLastWeekString,
  TodayString,
  YesterdayString
} from '@lla-platform/core/core-data-access'

@UntilDestroy()
@Component({
  selector: 'lla-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class DatePickerComponent implements OnInit, OnChanges, AfterViewInit {
  @ViewChild(MatCalendar) calendar: MatCalendar<Moment>

  @Input() value?: Date | string
  @Input() maxDate?: CalendarMaxDate = 'Today'
  @Input() minDate?: string
  @Input() label = 'Select date'

  @Output() valueChanged = new EventEmitter<Date>()
  @Output() cancelled = new EventEmitter()

  calendarMaxDate?: string
  dateForm: UntypedFormGroup
  selectedDate: Moment

  dateFormat = 'YYYY-MM-DD'
  validDateFormats = [this.dateFormat, 'YYYY-MM-D', 'YYYY-M-DD', 'YYYY-M-D']

  private dateControl: AbstractControl | null
  private dateTextControl: AbstractControl | null

  constructor(private fb: UntypedFormBuilder) {}

  ngOnInit(): void {
    switch (this.maxDate) {
      case 'Today':
        this.calendarMaxDate = TodayString()
        break
      case 'Yesterday':
        this.calendarMaxDate = YesterdayString()
        break
      case 'EndOFLastWeek':
        this.calendarMaxDate = EndOFLastWeekString()
        break
      default:
        this.calendarMaxDate = undefined
        break
    }

    const dateInRange = this.getInRangeDate()

    this.dateForm = this.fb.group({
      date: [dateInRange],
      dateText: [this.transformDate(dateInRange)]
    })

    this.dateControl = this.dateForm.get('date')
    this.dateTextControl = this.dateForm.get('dateText')
    this.selectedDate = dateInRange

    this.dateControl?.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      if (value) {
        this.dateTextControl?.setValue(this.transformDate(value), { emitEvent: false })
      }
    })

    this.dateTextControl?.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      let momentDate = moment(value, this.validDateFormats, true)
      if (!value || !momentDate.isValid()) {
        return
      }
      momentDate = momentDate.utcOffset(0, true)

      if (!momentDate.isAfter(this.calendarMaxDate)) {
        this.dateControl?.setValue(momentDate, { emitEvent: false })
        this.selectedDate = momentDate
        if (this.calendar) {
          this.calendar.activeDate = momentDate
        }
      }
    })
  }

  ngAfterViewInit(): void {
    if (this.calendar && this.selectedDate) {
      this.calendar.activeDate = this.selectedDate
    }
  }

  ngOnChanges(): void {
    const dateInRange = this.getInRangeDate()

    this.dateForm?.patchValue(
      {
        date: dateInRange,
        dateText: this.transformDate(dateInRange)
      },
      { emitEvent: false }
    )
    this.selectedDate = dateInRange
    if (this.calendar) {
      this.calendar.activeDate = dateInRange
    }
  }

  onSelectedDateChange(date: Moment): void {
    const momentDate = moment(date).utcOffset(0, true)
    this.dateControl?.setValue(momentDate)
    this.selectedDate = momentDate
  }

  fireValueChangedEvent(): void {
    const date = this.dateControl?.value
    if (date) {
      const output = isMoment(date) ? date.utc().toDate() : new Date(date)
      this.valueChanged.emit(output)
    }
  }

  transformDate(value?: Moment): string {
    return value ? value.format(this.dateFormat) : ''
  }

  private getInRangeDate(): Moment {
    const date = moment(this.value).utc()
    return date.isAfter(this.calendarMaxDate) ? moment(this.calendarMaxDate).utc() : date
  }
}
